import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// 分类很多且无固定语义：使用低饱和色盘作为轻量扫描信号，不影响卡片主体。
const CATEGORY_COLORS = [
  '#7f9db5', // 灰蓝
  '#71969e', // 青灰
  '#a08ba7', // 紫灰
  '#a38a68', // 暖棕
  '#778c74', // 灰绿
  '#87919d', // 中性灰
];

// 优化后的哈希函数：减少碰撞，分布更均匀
const getCategoryColor = (category) => {
  if (!category || category === '全部' || category === '其他') return '#87919d';
  
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    // 使用乘法哈希 (djb2 variant)，分布更均匀
    hash = ((hash << 5) - hash) + category.charCodeAt(i);
    hash |= 0; // 强制转换为32位整数
  }
  
  const index = Math.abs(hash) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[index];
};

const ExpertSelector = ({ 
  open, 
  onClose, 
  experts, // 这是过滤后的专家列表
  selectedExpert, 
  onExpertSelect,
  searchTerm,
  onSearchChange,
  selectedCategory,
  onCategorySelect,
  categories,
  accentColor = '#4D8BFF'
}) => {
  const { t, i18n } = useTranslation();
  const accent = accentColor || '#4D8BFF';

  const isZhCN = !i18n.language || i18n.language === 'zh-CN';

  // 获取专家显示名称（根据当前语言选择中文名或英文名）
  const getExpertDisplayName = (expert) => {
    if (!isZhCN && expert.display_name_en) return expert.display_name_en;
    return expert.display_name;
  };

  // 获取专家描述（根据当前语言选择中文或英文）
  const getExpertDescription = (expert) => {
    if (!isZhCN && expert.description_en) return expert.description_en;
    return expert.description;
  };

  // 获取单个专家的分类显示名（根据当前语言选择中文或英文）
  const getCategoryDisplayName = (expert) => {
    const cat = expert.parsedCategory;
    if (!isZhCN && cat?.base_category_en) return cat.base_category_en;
    return cat?.base_category;
  };

  // 构建分类中文→英文映射（用于分类筛选按钮显示）
  // 使用 useRef 缓存，避免筛选后丢失其他分类的英文名
  const categoryEnMapRef = useRef({});

  // 每次 experts 变化时，增量合并映射（只添加不覆盖，确保全量数据不丢失）
  useEffect(() => {
    experts?.forEach(expert => {
      const cat = expert.parsedCategory;
      if (cat?.base_category && cat?.base_category_en) {
        categoryEnMapRef.current[cat.base_category] = cat.base_category_en;
      }
    });
  }, [experts]);

  // 获取分类按钮显示文本
  const getCategoryButtonLabel = (category) => {
    if (category === '全部') return isZhCN ? '全部' : 'All';
    if (!isZhCN && categoryEnMapRef.current[category]) return categoryEnMapRef.current[category];
    return category;
  };
  // 使用 useRef 缓存全量统计数据
  const fullCategoryCounts = useRef({});

  // 计算当前视图中的分类统计
  const currentCounts = useMemo(() => {
    const counts = { '全部': experts?.length || 0 };
    experts?.forEach(expert => {
      const cat = expert.parsedCategory?.base_category;
      if (cat) {
        counts[cat] = (counts[cat] || 0) + 1;
      }
    });
    return counts;
  }, [experts]);

  // 当处于“无筛选”状态时，更新全量统计缓存
  useEffect(() => {
    if (selectedCategory === '全部' && !searchTerm) {
      fullCategoryCounts.current = currentCounts;
    }
  }, [currentCounts, selectedCategory, searchTerm]);

  // 获取用于显示的计数：优先使用缓存的全量计数，如果没有则回退到当前计数
  const getDisplayCount = (category) => {
    if (category === '全部') return currentCounts['全部']; 
    if (fullCategoryCounts.current[category] !== undefined) {
      return fullCategoryCounts.current[category];
    }
    return currentCounts[category] || 0;
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth={false}
      className="workbench-dialog expert-dialog"
      PaperProps={{
        className: 'workbench-dialog-paper',
        style: { '--workbench-accent': accent },
        sx: {
          width: 'calc(100vw - 60px)',
          maxWidth: '1400px',
          height: 'calc(100vh - 120px)',
          maxHeight: '700px',
          margin: '60px auto',
          bgcolor: '#1e1e1e',
          color: '#e7e7e7',
          borderRadius: '10px',
          border: '1px solid #3e3e42',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.38)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#202124',
        color: '#e7e7e7',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px',
        borderTop: `2px solid ${accent}`,
        borderBottom: '1px solid #3e3e42'
      }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#e7e7e7', lineHeight: 1.2 }}>
              {t('expert_dialog_title')}
            </Typography>
          </Box>
        </Box>
        
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: '#a6afb8',
            '&:hover': { color: '#fff', bgcolor: '#303238' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        bgcolor: '#1e1e1e',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          padding: '16px 24px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flexShrink: 0
        }}>
          {/* 搜索和筛选区域 */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* 搜索框 */}
            <div style={{ width: '240px', flexShrink: 0 }}>
              <input
                type="text"
                placeholder={t('expert_search_placeholder')}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => {
                  e.stopPropagation();
                  e.target.style.borderColor = accent;
                }}
                onBlur={(e) => e.target.style.borderColor = '#3e3e42'}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  border: '1px solid #3e3e42',
                  backgroundColor: '#292a2d',
                  color: '#e7e7e7',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* 分类筛选 */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', flex: '1' }}>
              {categories?.map(category => {
                const color = getCategoryColor(category);
                const isSelected = selectedCategory === category;
                const count = getDisplayCount(category);
                
                return (
                  <button
                    key={category}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCategorySelect(category);
                    }}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: `1px solid ${isSelected ? accent : `${color}66`}`,
                      backgroundColor: isSelected ? `${accent}1f` : '#292a2d',
                      color: isSelected ? accent : color,
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: isSelected ? '600' : '400',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {getCategoryButtonLabel(category)}
                    {count > 0 && (
                      <span style={{ 
                        fontSize: '11px', 
                        opacity: 0.9,
                        backgroundColor: isSelected ? accent : `${color}1f`,
                        color: isSelected ? '#fff' : color,
                        padding: '0 6px',
                        borderRadius: '10px',
                        height: '16px',
                        lineHeight: '16px',
                        display: 'inline-block',
                        minWidth: '16px',
                        textAlign: 'center'
                      }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </Box>

        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          padding: '16px 24px 24px',
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}>
          {/* 专家网格 */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '1350px',
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              columnGap: '12px',
              rowGap: '12px',
              justifyContent: 'center'
            }}
          >
            {experts?.map((expert) => {
              const isSelected = selectedExpert?.comd === expert.comd;
              const category = expert.parsedCategory?.base_category;
              const categoryColor = getCategoryColor(category);
              
              const borderColor = isSelected ? accent : '#3e3e42';
              const hoverBorderColor = isSelected ? accent : '#5b6068';

              return (
                <Paper
                  key={expert.comd}
                  onClick={() => onExpertSelect(expert)}
                  elevation={0}
                  sx={{
                    bgcolor: isSelected ? `${accent}1f` : '#292a2d',
                    border: `2px solid ${isSelected ? borderColor : '#334155'}`,
                    borderWidth: isSelected ? '2px' : '1px',
                    borderRadius: '8px',
                    p: 1.5,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'border-color 0.16s ease, background-color 0.16s ease',
                    minHeight: '96px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    opacity: 1,
                    '&:hover': {
                      borderColor: hoverBorderColor,
                      bgcolor: isSelected ? `${accent}2a` : '#303238'
                    }
                  }}
                >
                  {/* 头部区域：分类标签 + 标题（上下排列） */}
                  <Box sx={{ mb: 1 }}>
                    {/* 分类标签 */}
                    {category && category !== '其他' && (
                      <Box sx={{ mb: 0.5 }}>
                        <span style={{
                          backgroundColor: `${categoryColor}14`,
                          color: categoryColor,
                          fontSize: '9px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '600',
                          border: `1px solid ${categoryColor}66`,
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}>
                          {getCategoryDisplayName(expert) || category}
                        </span>
                      </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 700, 
                      color: isSelected ? accent : '#e7e7e7',
                      lineHeight: 1.4,
                      fontSize: '0.9rem',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {getExpertDisplayName(expert)}
                    </Typography>
                  </Box>
                  
                  {/* 描述文字 */}
                  <Typography variant="body2" sx={{ 
                    color: '#b8c0c8',
                    opacity: 1,
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    flex: 1,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }} title={getExpertDescription(expert) || t('expert_no_desc')}>
                    {getExpertDescription(expert) || t('expert_no_desc')}
                  </Typography>
                  
                  {/* 选中标记 */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    opacity: isSelected ? 1 : 0,
                    transition: 'opacity 0.16s ease',
                  }}>
                    <CheckCircleIcon sx={{ color: accent, fontSize: 18 }} />
                  </Box>
                </Paper>
              );
            })}
          </Box>

          {/* 无结果提示 */}
          {experts?.length === 0 && (
            <div style={{
              textAlign: 'center',
              color: '#64748b',
              padding: '40px',
              fontSize: '14px',
              width: '100%'
            }}>
              {t('expert_no_results')}
            </div>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExpertSelector;
